export type * from "./types.ts";

import type { MaybePromise, PickPartial } from "./types.ts";

export function clamp(value: number, min: number, max: number) {
  return Math.min(Math.max(value, min), max);
}

export function msToNs(ms: number) {
  return Math.round(ms * 10e6);
}

export const numFormatter = new Intl.NumberFormat("en", {
  maximumFractionDigits: 2,
});

export const shortNumFormatter = new Intl.NumberFormat("en", {
  notation: "compact",
  maximumFractionDigits: 2,
});

export const unsafeKeys: <T extends object>(obj: T) => Array<keyof T> = Object.keys;
export const unsafeEntries: <T extends object>(obj: T) => Array<[keyof T, T[keyof T]]> =
  Object.entries;
export const unsafeFromEntries: <
  const TEntries extends ReadonlyArray<readonly [PropertyKey, unknown]>,
>(
  entries: TEntries,
) => {
  -readonly [TEntry in TEntries[number] as TEntry[0]]: TEntry[1];
} = Object.fromEntries;

export function partition<T, U extends T>(
  array: ReadonlyArray<T>,
  predicate: (value: T) => value is U,
): [truthy: Array<U>, falsy: Array<Exclude<T, U>>];
export function partition<T>(
  array: ReadonlyArray<T>,
  predicate: (value: T) => boolean,
): [truthy: Array<T>, falsy: Array<T>];
export function partition<T>(array: ReadonlyArray<T>, predicate: (value: T) => boolean) {
  const results: [truthy: Array<T>, falsy: Array<T>] = [[], []];
  for (const value of array) {
    results[predicate(value) ? 0 : 1].push(value);
  }
  return results;
}

export function getOrInsert<K extends object, V>(
  map: WeakMap<K, V>,
  key: NoInfer<K>,
  value: NoInfer<V>,
): NoInfer<V>;
export function getOrInsert<K, V>(map: Map<K, V>, key: NoInfer<K>, value: NoInfer<V>): NoInfer<V>;
export function getOrInsert<K extends object, V>(
  map: Map<K, V> | WeakMap<K, V>,
  key: NoInfer<K>,
  value: NoInfer<V>,
): NoInfer<V> {
  if (map.has(key)) return map.get(key) as V;

  return map.set(key, value).get(key) as V;
}

export function getOrInsertComputed<K extends object, V>(
  map: WeakMap<K, V>,
  key: NoInfer<K>,
  compute: (key: K) => NoInfer<V>,
): NoInfer<V>;
export function getOrInsertComputed<K, V>(
  map: Map<K, V>,
  key: NoInfer<K>,
  compute: (key: K) => NoInfer<V>,
): NoInfer<V>;
export function getOrInsertComputed<K extends object, V>(
  map: Map<K, V> | WeakMap<K, V>,
  key: NoInfer<K>,
  compute: (key: K) => NoInfer<V>,
): NoInfer<V> {
  if (map.has(key)) return map.get(key) as V;

  return map.set(key, compute(key)).get(key) as V;
}

const byteUnits = ["B", "KB", "MB", "GB", "TB", "PB", "EB", "ZB", "YB"];
export function formatBytes(bytes: number, formatter = numFormatter) {
  if (bytes < 1) return "0 B";
  const unit = Math.floor(Math.log2(bytes) / 10);
  return `${formatter.format(bytes / 2 ** (10 * unit))} ${byteUnits[unit]}`;
}

export const longDateFormatter = new Intl.DateTimeFormat("en", {
  dateStyle: "long",
});

export const durationFormatter = new Intl.DurationFormat("en");

const units: Array<[threshold: number, Intl.DurationFormatUnit]> = [
  [3_600_000, "hours"],
  [60_000, "minutes"],
  [1_000, "seconds"],
  [1, "milliseconds"],
  [0.001, "microseconds"],
  [0.000_001, "nanoseconds"],
];

export const getDuration = (
  ms: number,
  unitCount = 1,
): Partial<Record<Intl.DurationFormatUnit, number>> => {
  const result: Partial<Record<Intl.DurationFormatUnit, number>> = {};
  let remaining = ms;
  let added = 0;

  for (const [threshold, unit] of units) {
    if (remaining >= threshold) {
      const isLast = added === unitCount - 1;
      const value = isLast ? Math.round(remaining / threshold) : Math.floor(remaining / threshold);
      result[unit] = value;
      remaining -= value * threshold;
      added++;
      if (added >= unitCount) break;
    }
  }

  if (added === 0) return { nanoseconds: Math.round(ms * 1_000_000) };
  return result;
};

const enumerableKeys = (obj: object) =>
  Reflect.ownKeys(obj).filter((key) => Object.getOwnPropertyDescriptor(obj, key)?.enumerable);

export const promiseAllKeyed = async <T extends Record<PropertyKey, unknown>>(
  keyed: T,
): Promise<{
  [K in keyof T]: Awaited<T[K]>;
}> =>
  unsafeFromEntries(
    await Promise.all(enumerableKeys(keyed).map(async (key) => [key, await keyed[key]])),
  ) as never;

export const promiseAllSettledKeyed = async <T extends Record<PropertyKey, unknown>>(
  keyed: T,
): Promise<{
  [K in keyof T]: PromiseSettledResult<Awaited<T[K]>>;
}> => {
  const keys = enumerableKeys(keyed);
  const results = await Promise.allSettled(keys.map((key) => keyed[key]));
  return unsafeFromEntries(results.map((result, i) => [keys[i]!, result])) as never;
};

/**
 * Sets an interval that is automatically cleared when the signal is aborted.
 * @param fn The function to call every `ms` milliseconds.
 * @param delay The number of milliseconds to wait between each call.
 * @param signal The abort signal to use.
 * @returns The interval ID.
 * @see {setInterval}
 */

export const setAbortableInterval = <TArgs extends Array<any>>(
  fn: (...args: TArgs) => void,
  delay: number,
  signal?: AbortSignal,
  ...args: TArgs
): ReturnType<typeof setInterval> => {
  const interval = setInterval(fn, delay, ...args);
  signal?.addEventListener("abort", () => clearInterval(interval), {
    once: true,
  });
  return interval;
};

/**
 * Sets a timeout that is automatically cleared when the signal is aborted.
 * @param fn The function to call after the timeout.
 * @param delay The number of milliseconds to wait before calling the function.
 * @param signal The abort signal to use.
 * @returns The timeout ID.
 * @see {setTimeout}
 */
export const setAbortableTimeout = <TArgs extends Array<any>>(
  fn: (...args: TArgs) => void,
  delay: number,
  signal?: AbortSignal,
  ...args: TArgs
): ReturnType<typeof setTimeout> => {
  const timeout = setTimeout(fn, delay, ...args);
  signal?.addEventListener("abort", () => clearTimeout(timeout), {
    once: true,
    signal: AbortSignal.timeout(delay),
  });
  return timeout;
};

export type TupleOfLength<T, N extends number, Acc extends Array<T> = []> = Acc["length"] extends N
  ? Acc
  : TupleOfLength<T, N, [T, ...Acc]>;

export function hasLength<T, N extends number>(
  array: Array<T>,
  length: N,
): array is TupleOfLength<T, N> {
  return array.length === length;
}

export type TupleOfAtLeast<T, N extends number> = TupleOfLength<T, N> & Array<T>;

export function hasAtLeast<T, N extends number>(
  array: Array<T>,
  length: N,
): array is TupleOfAtLeast<T, N> {
  return array.length >= length;
}

export function isPlainObject(obj: unknown): obj is object {
  if (typeof obj !== "object" || obj === null) return false;

  let proto = obj;
  while (Object.getPrototypeOf(proto) !== null) {
    proto = Object.getPrototypeOf(proto);
  }

  return Object.getPrototypeOf(obj) === proto || Object.getPrototypeOf(obj) === null;
}

export function serialize(value: unknown): string {
  return JSON.stringify(value, (_key, innerValue) => {
    // sort the object keys before serialization, so { a: 1, b: 2 } === { b: 2, a: 1 }
    if (isPlainObject(innerValue)) {
      return Object.fromEntries(
        // oxlint-disable-next-line unicorn/no-array-sort
        Object.entries(innerValue).sort(([a], [b]) => a.localeCompare(b)),
      );
    }
    return innerValue;
  });
}

export const ensureArray = <T>(value: T) =>
  (Array.isArray(value) ? value : [value]) as T extends ReadonlyArray<unknown> ? T : Array<T>;

export const anyAbortSignal = (...signals: Array<AbortSignal | undefined>) =>
  AbortSignal.any(signals.filter((s) => !!s));

export function toggleFilter<K extends string, const V extends string>(
  key: K,
  newValue: V,
  defaultValue: V,
): <T extends Record<K, V>>(filter: T) => T;
export function toggleFilter<K extends string, const V extends string>(
  key: K,
  newValue: V,
  defaultValue?: V,
): <T extends Partial<Record<K, V>>>(filter: T) => PickPartial<T, K>;
export function toggleFilter(key: string, newValue: unknown, defaultValue?: unknown) {
  return (filter: Record<string, unknown>) => ({
    ...filter,
    [key]: filter[key] === newValue ? defaultValue : newValue,
  });
}

/**
 * Filter an array of objects by a set of key-value pairs.
 * @param filter The key-value pairs to filter by.
 * @returns A function that takes an object and returns true if it matches the filter.
 * @example
 * const array = [{ a: 1, b: 2 }, { a: 1, b: 3 }, { a: 2, b: 2 }];
 * const result = array.filter(shallowFilter({ a: [1, 2], b: 2 }));
 * // [{ a: 1, b: 2 }, { a: 2, b: 2 }]
 */
export function shallowFilter<T>(filter: {
  [K in keyof T]?: T[K] | ReadonlyArray<T[K]> | Set<T[K]>;
}): (item: T) => boolean {
  const entries = unsafeEntries(filter).map(
    ([key, value]) => [key, Array.isArray(value) ? new Set(value) : value] as const,
  );
  return (item) => {
    for (const [key, value] of entries) {
      if (value === undefined) continue;
      if (value instanceof Set) {
        if (!value.has(item[key])) return false;
      } else if (item[key] !== value) return false;
    }
    return true;
  };
}

export function getTransitionName(prefix: string, values: Record<string, unknown>) {
  let name = prefix;
  for (const [key, value] of Object.entries(values)) {
    name += `-${key}-${String(value)}`;
  }
  return name.replace(/[^a-zA-Z0-9]/g, "-");
}

export function uniqueBy<T>(
  values: ReadonlyArray<T>,
  getKey: (value: T) => unknown = (value) => value,
): Array<T> {
  const keys = new Set();
  return values.filter((value) => {
    const key = getKey(value);
    if (keys.has(key)) return false;
    keys.add(key);
    return true;
  });
}

export function assert(condition: unknown, message?: string): asserts condition {
  if (!condition) throw new Error(message);
}

export function assertNever(_value: never, message: string): never {
  throw new Error(message);
}

export const nonNullish = <T>(value: T) => value != null;

export const collator = new Intl.Collator(undefined, {
  numeric: true,
  sensitivity: "base",
});

// we only support English (other languages have different types of plural we don't support)
const pluralRules = new Intl.PluralRules("en");

type PluralizeTuple = [count: number, singular: string, plural?: string];
function isPluralizeTuple(value: unknown): value is PluralizeTuple {
  return Array.isArray(value) && value.length >= 2 && value.length <= 3;
}

/**
 * A tagged template literal that pluralizes the word based on the count.
 * If unspecified, the plural form is the singular form + `s`.
 * @example
 * pluralize`I have ${count} ${[count, "apple"]}.`
 * pluralize`I have ${count} ${[count, "goose", "geese"]}.`
 */
export function pluralize(strings: TemplateStringsArray, ...values: Array<unknown>) {
  let result = "";
  for (const i of range(0, strings.length)) {
    result += strings[i];
    if (i < values.length) {
      const value = values[i];
      if (isPluralizeTuple(value)) {
        const [count, singular, plural = `${singular}s`] = value;
        result += pluralRules.select(count) === "one" ? singular : plural;
      } else {
        result += value;
      }
    }
  }
  return result;
}

export function promiseTry<T>(fn: () => MaybePromise<T>): Promise<T> {
  return new Promise((resolve) => resolve(fn()));
}

export const exclude = Symbol("exclude");

/**
 * Filter and map an array.
 * @param array The array to filter and map.
 * @param mapper The function to call for each element. Return `exclude` to exclude the element.
 * @returns A new array with the mapped values.
 */
export function filterMap<T, U>(
  array: ReadonlyArray<T>,
  mapper: (value: T) => U | typeof exclude,
): Array<U> {
  const filtered: Array<U> = [];
  for (const value of array) {
    const mapped = mapper(value);
    if (mapped !== exclude) filtered.push(mapped);
  }
  return filtered;
}

export const safeAssign: <T extends object>(target: T, ...sources: Array<Partial<T>>) => T =
  Object.assign;

export function makeDisposable<T extends { unsubscribe: () => void }>(value: T): T & Disposable {
  return {
    ...value,
    [Symbol.dispose]: value.unsubscribe,
  };
}

export const capitalize = <S extends string>(str: S): Capitalize<S> =>
  (str.charAt(0).toUpperCase() + str.slice(1)) as never;

interface RangeOptions {
  step?: number;
  inclusive?: boolean;
}
export function* range(
  start: number,
  end: number,
  { step = 1, inclusive = false }: RangeOptions = {},
): Generator<number> {
  if (step <= 0) {
    throw new RangeError("step must be a positive number");
  }
  if (start < end) {
    for (let i = start; i < end; i += step) {
      yield i;
    }
    if (inclusive && (end - start) % step === 0) {
      yield end;
    }
  } else {
    for (let i = start; i > end; i -= step) {
      yield i;
    }
    if (inclusive && (start - end) % step === 0) {
      yield end;
    }
  }
}

export function identity<T>(value: T): T {
  return value;
}

/**
 * Hash function that returns a 53-bit hash of a string, with an optional prefix (defaulting to the string length).
 * @remarks Fast and deterministic. Not suitable for cryptographic purposes.
 */
export function getCyrb53Hash(str: string, prefix = str.length.toString()): string {
  let h1 = 0xdeadbeef ^ str.length;
  let h2 = 0x41c6ce57 ^ str.length;

  for (let i = 0; i < str.length; i++) {
    const ch = str.charCodeAt(i);
    h1 = Math.imul(h1 ^ ch, 2654435761);
    h2 = Math.imul(h2 ^ ch, 1597334677);
  }

  h1 = Math.imul(h1 ^ (h1 >>> 16), 2246822507) ^ Math.imul(h2 ^ (h2 >>> 13), 3266489909);
  h2 = Math.imul(h2 ^ (h2 >>> 16), 2246822507) ^ Math.imul(h1 ^ (h1 >>> 13), 3266489909);

  const hash53 = 4294967296 * (2097151 & h2) + (h1 >>> 0);

  return `${prefix}-${hash53.toString(36)}`;
}
